// Driver Loans: airtime, cash, a repair allocated to a driver, Uber cash he did
// not pay over, and anything else he owes, each confirmed by him on SignNow
// before a rand comes off his pay. db/migrations/0360.
//
// Every figure on this screen comes from Postgres: the balance, the schedule,
// the first payday, what is due on the next payslip. The page arranges them.
// CLAUDE.md rule 4.
//
// THE ORDER IS THE POINT. Raised, then signed, then paid out, then deducted.
// Airtime and cash are marked paid only after the signature, and only by the
// person who releases payments (can_release_loan_payments, 0361), who is
// emailed the moment the driver signs. The button does not exist before it. A request the driver declines is kept, not deleted,
// because the record that he said no matters as much as the record that he said
// yes.
//
// Open to Edit drivers and to the people who run pay (driver_loans_allowed_v1).
// Recording what payroll deducted is for the second group only.
import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { AlertTriangle, ChevronDown, ChevronRight, ExternalLink, HandCoins, RefreshCw, Send, ShieldAlert, X } from 'lucide-react'
import {
  useCancelLoan, useCheckLoanSignatures, useGetLoanDocumentLink, useGetLoanOptions, useGetLoanProofUrl, useGetLoans,
  useGetLoansAccess, useGetLoansDue, useMarkLoanPaidOut, useRecordLoanDeduction, useResendLoan,
} from '../hooks/backend/loans'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { RaiseLoanDialog } from '../components/loans/RaiseLoanDialog'
import {
  STATUS_LABEL, STATUS_TONE, formatDay, formatWhen, formatZar,
  type DueResult, type ListResult, type Loan, type Options,
} from '../utils/loans'

const FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Waiting to be signed' },
  { value: 'to_pay_out', label: 'Signed, ready to pay' },
  { value: 'signed', label: 'Signed' },
  { value: 'declined', label: 'Declined' },
  { value: 'cancelled', label: 'Cancelled' },
]

export default function DriverLoans() {
  const accessQuery = useGetLoansAccess<{ allowed: boolean }>()
  const optionsQuery = useGetLoanOptions<Options>()
  const listQuery = useGetLoans<ListResult>()
  const dueQuery = useGetLoansDue<DueResult>()
  const check = useCheckLoanSignatures<{ checked: number; signed: number; declined: number }>()

  const [status, setStatus] = useState('')
  const [driver, setDriver] = useState('')
  const [raising, setRaising] = useState(false)
  // ?loan= is the link in the "ready to pay" email, so it opens on that loan.
  const [params] = useSearchParams()
  const [expanded, setExpanded] = useState<string | null>(params.get('loan'))
  const [showDue, setShowDue] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const loadAccess = accessQuery.trigger
  const loadOptions = optionsQuery.trigger
  const loadList = listQuery.trigger
  const loadDue = dueQuery.trigger
  const runCheck = check.trigger

  useEffect(() => { void loadAccess({}, { skipCache: true }).catch(() => undefined) }, [loadAccess])
  const allowed = accessQuery.data?.allowed === true
  const options = optionsQuery.data ?? null

  const refreshList = useCallback(() => {
    void loadList({ status: status || null, driver_id: driver || null }, { skipCache: true }).catch(() => undefined)
  }, [loadList, status, driver])
  const refreshDue = useCallback(() => {
    void loadDue({}, { skipCache: true }).catch(() => undefined)
  }, [loadDue])

  useEffect(() => { if (allowed) void loadOptions({}, { skipCache: true }).catch(() => undefined) }, [allowed, loadOptions])
  useEffect(() => { if (allowed) refreshList() }, [allowed, refreshList])
  useEffect(() => { if (allowed && showDue) refreshDue() }, [allowed, showDue, refreshDue])

  // Opening the page asks SignNow once, so a driver who signed while the
  // manager was driving over shows as signed without waiting for the cron.
  useEffect(() => {
    if (!allowed) return
    void runCheck({}).then((r) => {
      if (r && (r.signed > 0 || r.declined > 0)) refreshList()
    }).catch(() => undefined)
  }, [allowed, runCheck, refreshList])

  async function checkNow() {
    setNotice(null)
    try {
      const r = await runCheck({})
      refreshList()
      if (r) {
        setNotice(r.checked === 0
          ? 'Nothing is waiting for a signature.'
          : `Checked ${r.checked} with SignNow: ${r.signed} newly signed, ${r.declined} declined, the rest still waiting.`)
      }
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e))
    }
  }

  const rows = listQuery.data?.rows ?? []
  const counts = listQuery.data?.counts

  if (accessQuery.loading && !accessQuery.data && !accessQuery.error) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Checking access…</div>
  }
  if (accessQuery.error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{accessQuery.error}</p>
          <Button size="sm" variant="outline" onClick={() => void loadAccess({}, { skipCache: true }).catch(() => undefined)}>Try again</Button>
        </div>
      </div>
    )
  }
  if (!allowed) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">Driver loans are open to driver managers and the people who run pay.</p>
          <p className="text-xs text-muted-foreground">If you should be one of them, ask an admin to switch on Edit drivers for you in cockpit access.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <HandCoins className="h-5 w-5" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <h1 className="text-lg font-semibold text-foreground">Driver Loans</h1>
          <p className="text-xs text-muted-foreground">
            Airtime, cash, repairs and Uber cash a driver owes. He confirms each one on SignNow before anything comes off his pay.
            {options?.next_payday ? ` Signed today, it comes off the payslip of ${options.next_payday}.` : ''}
          </p>
        </div>
        {/* Refunds live on their own page: money Fleetly holds for someone, never a loan. */}
        <Button asChild variant="outline" size="sm">
          <Link to="/driver-refunds">Refunds</Link>
        </Button>
        <Button variant="outline" size="sm" onClick={() => void checkNow()} disabled={check.loading}>
          {check.loading ? <Spinner className="mr-1 h-4 w-4" /> : <RefreshCw className="mr-1 h-4 w-4" />} Check signatures
        </Button>
        <Button variant="brand" size="sm" onClick={() => setRaising(true)} disabled={!options}>
          <HandCoins className="mr-1 h-4 w-4" /> Raise a loan
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        {counts && (
          <>
            <Chip tone={counts.awaiting_signature ? 'amber' : 'neutral'}>{counts.awaiting_signature} waiting to be signed</Chip>
            {counts.send_failed > 0 && <Chip tone="red" icon={<AlertTriangle className="h-3 w-3" />}>{counts.send_failed} not sent</Chip>}
            <Chip tone={counts.to_pay_out ? 'brand' : 'neutral'}>{counts.to_pay_out} signed, ready to pay</Chip>
            <Chip tone="neutral">{formatZar(counts.outstanding_zar)} still owed on signed loans</Chip>
          </>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <NativeSelect className="h-8 w-44 text-xs" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
            {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </NativeSelect>
          <NativeSelect className="h-8 w-48 text-xs" value={driver} onChange={(e) => setDriver(e.target.value)} aria-label="Driver">
            <option value="">Every driver</option>
            {(options?.drivers ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </NativeSelect>
        </div>
      </div>

      {notice && (
        <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-2 text-xs text-foreground">
          <span className="flex-1">{notice}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {listQuery.error && <div className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{listQuery.error}</div>}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <DuePanel open={showDue} onToggle={() => setShowDue((v) => !v)} due={dueQuery.data ?? null} loading={dueQuery.loading} error={dueQuery.error ?? null} />

        {listQuery.loading && !listQuery.data ? (
          <p className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading loans…</p>
        ) : rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            {status || driver ? 'No loan matches these filters.' : 'No loans yet. Raise one with the button above.'}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((l) => (
              <LoanItem
                key={l.id}
                loan={l}
                open={expanded === l.id}
                onToggle={() => setExpanded((x) => (x === l.id ? null : l.id))}
                canRecordPay={options?.can_record_pay === true}
                canRelease={options?.can_release === true}
                releasers={options?.releasers ?? 'Luyanda'}
                proofAddress={options?.proof_address ?? 'admin@fleetly.co.za'}
                onChanged={() => { refreshList(); if (showDue) refreshDue() }}
              />
            ))}
          </ul>
        )}
      </div>

      <RaiseLoanDialog
        open={raising}
        onOpenChange={setRaising}
        options={options}
        presetDriverId={driver || null}
        onSaved={() => refreshList()}
      />
    </div>
  )
}

function DuePanel({ open, onToggle, due, loading, error }: {
  open: boolean; onToggle: () => void; due: DueResult | null; loading: boolean; error: string | null
}) {
  return (
    <section className="border-b border-border bg-muted/20">
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs font-medium text-foreground">
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        For payroll: what comes off the next payslip{due ? `, ${due.payday}` : ''}
      </button>
      {open && (
        <div className="px-4 pb-3">
          {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}
          {loading && !due ? (
            <p className="flex items-center gap-2 text-xs text-muted-foreground"><Spinner className="h-3 w-3" /> Loading…</p>
          ) : due && due.rows.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nothing signed is due on the payslip of {due.payday}.</p>
          ) : due ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[32rem] text-xs">
                <thead className="text-left text-muted-foreground">
                  <tr><th className="py-1 pr-3 font-medium">Driver</th><th className="py-1 pr-3 font-medium">Loan</th><th className="py-1 pr-3 font-medium">SimplePay item</th><th className="py-1 pr-3 text-right font-medium">Deduct</th><th className="py-1 font-medium" /></tr>
                </thead>
                <tbody>
                  {due.rows.map((r) => (
                    <tr key={r.loan_id} className="border-t border-border">
                      <td className="py-1 pr-3">{r.driver_name}</td>
                      <td className="py-1 pr-3">{r.ref}, {r.category_label}</td>
                      <td className="py-1 pr-3">{r.simplepay_item}</td>
                      <td className="py-1 pr-3 text-right font-medium tabular-nums">{formatZar(r.due_zar)}</td>
                      <td className="py-1">{r.carried_from_earlier && <Chip tone="amber">includes an earlier payslip</Chip>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-[11px] text-muted-foreground">
                These reach SimplePay by hand, as loan and repair recoveries do today. Once a payslip is run, record what was actually deducted on each loan so the balance moves.
              </p>
            </div>
          ) : null}
        </div>
      )}
    </section>
  )
}

function LoanItem({ loan: l, open, onToggle, canRecordPay, canRelease, releasers, proofAddress, onChanged }: {
  loan: Loan; open: boolean; onToggle: () => void; canRecordPay: boolean; canRelease: boolean
  releasers: string; proofAddress: string; onChanged: () => void
}) {
  const resend = useResendLoan()
  const cancel = useCancelLoan<{ invite_withdrawn: boolean; was_sent: boolean }>()
  const paidOut = useMarkLoanPaidOut()
  const record = useRecordLoanDeduction()
  const link = useGetLoanDocumentLink<{ url: string }>()
  const proof = useGetLoanProofUrl<{ url: string }>()
  const [error, setError] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [payNote, setPayNote] = useState('')
  const [deduct, setDeduct] = useState<Record<string, string>>({})

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try { await fn(); onChanged() } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }

  // Opened before the await for the same reason as openDocument below.
  async function openProof(id: string) {
    setError(null)
    const w = window.open('', '_blank')
    try {
      const r = await proof.trigger({ id }, { skipCache: true })
      if (r?.url && w) w.location.href = r.url
      else w?.close()
    } catch (e) {
      w?.close()
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function openDocument() {
    setError(null)
    // Opened before the await so the browser treats it as the click's own window
    // and does not block it as a popup.
    const w = window.open('', '_blank')
    try {
      const r = await link.trigger({ id: l.id }, { skipCache: true })
      if (r?.url && w) w.location.href = r.url
      else w?.close()
    } catch (e) {
      w?.close()
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const needsPayout = l.status === 'signed' && l.pays_out && !l.paid_out_at
  const vehicleText = l.no_vehicle ? 'No vehicle involved' : l.vehicle ?? '—'

  return (
    <li>
      <button type="button" onClick={onToggle} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-accent/40">
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <span className="w-16 shrink-0 font-mono text-xs text-muted-foreground">{l.ref}</span>
        <span className="min-w-[10rem] flex-1">
          <span className="block text-sm font-medium text-foreground">{l.driver_name}</span>
          <span className="block text-xs text-muted-foreground">{l.category_label}{l.instalments > 1 ? `, ${l.instalments} payslips` : ', one payslip'}</span>
        </span>
        <span className="w-24 text-right text-sm font-semibold tabular-nums text-foreground">{formatZar(l.amount_zar)}</span>
        <span className="flex w-full flex-wrap justify-end gap-1 sm:w-auto">
          <Chip tone={STATUS_TONE[l.status]}>{STATUS_LABEL[l.status]}</Chip>
          {needsPayout && <Chip tone="brand">ready to pay</Chip>}
          {l.status === 'signed' && l.pays_out && l.paid_out_at && <Chip tone="green">paid</Chip>}
        </span>
      </button>

      {open && (
        <div className="space-y-3 bg-muted/20 px-4 pb-4 pl-11 text-sm">
          <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            <Row k="What it is for" v={l.reason} />
            {l.phone_number && <Row k="Cell number" v={l.phone_number} />}
            <Row k="Vehicle" v={vehicleText} />
            {l.event_date && <Row k="Date it happened" v={formatDay(l.event_date)} />}
            <Row k="Raised by" v={`${l.requested_by_name}, ${formatWhen(l.requested_at)}`} />
            {l.terms_reason && <Row k="Why it is spread" v={l.terms_reason} />}
            {l.sent_at && <Row k="Sent to sign" v={`${formatWhen(l.sent_at)}, to ${l.driver_email ?? '—'}`} />}
            {l.signed_at && <Row k="Signed" v={formatWhen(l.signed_at)} />}
            {l.declined_at && <Row k="Declined" v={formatWhen(l.declined_at)} />}
            {l.first_payday && <Row k="First payslip" v={l.first_payday} />}
            {l.status === 'signed' && <Row k="Still owed" v={formatZar(l.balance_zar)} />}
            {l.paid_out_at && <Row k="Paid" v={`${formatWhen(l.paid_out_at)} by ${l.paid_out_by_name ?? '—'}, ${l.paid_out_method === 'emailed_proof' ? 'proof of payment emailed' : 'marked paid'}${l.paid_out_note && l.paid_out_method !== 'emailed_proof' ? `: ${l.paid_out_note}` : ''}`} />}
            {l.notified_at && l.pays_out && <Row k="Payment requested" v={`${releasers} emailed ${formatWhen(l.notified_at)}`} />}
            {l.cancelled_at && <Row k="Cancelled" v={`${formatWhen(l.cancelled_at)}: ${l.cancel_reason ?? ''}`} />}
            {l.status === 'awaiting_signature' && <Row k="Last checked with SignNow" v={formatWhen(l.last_checked_at)} />}
          </dl>

          {l.status === 'send_failed' && l.send_error && (
            <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{l.send_error}</p>
          )}
          {l.status === 'declined' && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
              He declined it, so nothing will be deducted. Speak to him; if the details were wrong, raise a corrected one.
            </p>
          )}

          {needsPayout && canRelease && (
            <div className="space-y-2 rounded-md border border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] p-3 text-xs text-[hsl(var(--brand-soft-foreground))]">
              <p className="text-sm font-semibold">
                {l.category === 'airtime'
                  ? `Load ${formatZar(l.amount_zar)} of airtime or data on ${l.phone_number ?? 'his number'}.`
                  : `Pay ${formatZar(l.amount_zar)} to ${l.driver_name}.`}
              </p>
              <p>
                He signed {formatWhen(l.signed_at)}. When it is paid, press I have paid, or email the proof of payment to {proofAddress} with {l.ref} in the subject. Replying to the email you were sent does exactly that.
              </p>
              <span className="flex flex-wrap items-center gap-1">
                <Input className="h-8 w-60 bg-background text-xs" placeholder={l.category === 'airtime' ? 'Optional note, e.g. Loaded via FNB app' : 'Optional note, e.g. EFT from FNB'}
                  value={payNote} onChange={(e) => setPayNote(e.target.value)} />
                <Button size="sm" variant="brand" disabled={paidOut.loading} onClick={() => void run(() => paidOut.trigger({ id: l.id, note: payNote }))}>
                  {paidOut.loading ? <Spinner className="mr-1 h-3.5 w-3.5" /> : null} I have paid
                </Button>
              </span>
            </div>
          )}
          {needsPayout && !canRelease && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
              Signed. Waiting for {releasers} to pay{l.notified_at ? `, who was emailed ${formatWhen(l.notified_at)}` : ''}. Do not hand anything over until it shows paid.
            </p>
          )}
          {canRelease && l.pays_out && (l.status === 'awaiting_signature' || l.status === 'sending') && (
            <p className="text-xs text-muted-foreground">You can pay this once he has signed. You will be emailed the moment he does.</p>
          )}

          {l.proofs.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-foreground">Proof of payment</span>
              <ul className="space-y-1">
                {l.proofs.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-2 text-xs">
                    <Button size="sm" variant="outline" className="h-7" disabled={proof.loading} onClick={() => void openProof(p.id)}>
                      <ExternalLink className="mr-1 h-3.5 w-3.5" /> {p.filename ?? 'Attachment'}
                    </Button>
                    <span className="text-muted-foreground">emailed {formatWhen(p.received_at)}{p.from_email ? ` by ${p.from_email}` : ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {l.schedule && l.schedule.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[26rem] text-xs">
                <thead className="text-left text-muted-foreground">
                  <tr><th className="py-1 pr-3 font-medium">Payslip</th><th className="py-1 pr-3 text-right font-medium">Due</th><th className="py-1 pr-3 text-right font-medium">Deducted</th>{canRecordPay && <th className="py-1 font-medium" />}</tr>
                </thead>
                <tbody>
                  {l.schedule.map((s) => (
                    <tr key={s.no} className="border-t border-border">
                      <td className="py-1 pr-3">{s.payday}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{formatZar(s.scheduled_zar)}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{s.recorded_zar > 0 ? formatZar(s.recorded_zar) : '—'}</td>
                      {canRecordPay && (
                        <td className="py-1">
                          <span className="flex items-center gap-1">
                            <Input className="h-7 w-24 text-xs" inputMode="decimal" placeholder={String(s.scheduled_zar)}
                              value={deduct[s.pay_month] ?? ''} onChange={(e) => setDeduct((d) => ({ ...d, [s.pay_month]: e.target.value }))} />
                            <Button size="sm" variant="outline" className="h-7" disabled={record.loading || !(deduct[s.pay_month] ?? '').trim()}
                              onClick={() => void run(() => record.trigger({ id: l.id, pay_month: s.pay_month, amount_zar: (deduct[s.pay_month] ?? '').replace(',', '.') }))}>
                              Record
                            </Button>
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {l.has_document && (
              <Button size="sm" variant="outline" onClick={() => void openDocument()} disabled={link.loading}>
                <ExternalLink className="mr-1 h-3.5 w-3.5" /> {l.status === 'signed' ? 'Open the signed page' : 'Open the page he was sent'}
              </Button>
            )}
            {l.status === 'send_failed' && (
              <Button size="sm" variant="brand" disabled={resend.loading} onClick={() => void run(() => resend.trigger({ id: l.id }))}>
                {resend.loading ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <Send className="mr-1 h-3.5 w-3.5" />} Send again
              </Button>
            )}
            {(l.status === 'awaiting_signature' || l.status === 'send_failed' || l.status === 'sending') && !cancelling && (
              <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}>Cancel this loan</Button>
            )}
            {cancelling && (
              <span className={cn('flex flex-wrap items-center gap-1')}>
                <Input className="h-8 w-64 text-xs" placeholder="Why it is being cancelled" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                <Button size="sm" variant="destructive" disabled={cancel.loading}
                  onClick={() => void run(async () => { await cancel.trigger({ id: l.id, reason: cancelReason }); setCancelling(false) })}>
                  Cancel it
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setCancelling(false)}>Keep it</Button>
              </span>
            )}
          </div>
          {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
        </div>
      )}
    </li>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-36 shrink-0 text-muted-foreground">{k}</dt>
      <dd className="min-w-0 break-words text-foreground">{v}</dd>
    </div>
  )
}
